import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { FadeIn } from '../components/eclora/Reveal';

const linkify = (text) =>
    text.split(/(https?:\/\/[^\s]+|contact@mon-eclora\.fr)/g).map((part, i) => {
        if (/^https?:\/\//.test(part)) {
            return (
                <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-bordeaux underline decoration-champagne/60 underline-offset-2 transition-colors duration-300 hover:text-bordeaux-deep">
                    {part}
                </a>
            );
        }
        if (part === 'contact@mon-eclora.fr') {
            return (
                <a key={i} href="mailto:contact@mon-eclora.fr" className="text-bordeaux underline decoration-champagne/60 underline-offset-2 transition-colors duration-300 hover:text-bordeaux-deep">
                    {part}
                </a>
            );
        }
        return part;
    });

export default function LegalPage({ title, updated, intro, sections, otherHref, otherLabel }) {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-creme font-sans text-cacao" data-testid="legal-page">
            <header className="border-b border-champagne/40">
                <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5">
                    <a href="/" aria-label="Retour à l'accueil Mon Éclora" data-testid="legal-logo-link">
                        <img src="/assets/phoenix.png" alt="Phénix Éclora" className="w-12 mix-blend-multiply" />
                    </a>
                    <a
                        href="/"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-cacao/70 transition-colors duration-300 hover:text-bordeaux"
                        data-testid="legal-back-link"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                        Retour à l'accueil
                    </a>
                </div>
            </header>

            <article className="mx-auto max-w-3xl px-6 py-14 sm:py-20">
                <FadeIn y={18}>
                    <p className="eyebrow">Mon Éclora</p>
                    <h1 className="mt-4 font-serif text-3xl font-medium leading-tight text-bordeaux sm:text-4xl lg:text-5xl" data-testid="legal-title">
                        {title}
                    </h1>
                    <p className="mt-4 text-[11px] uppercase tracking-[0.28em] text-champagne" data-testid="legal-updated">
                        Dernière mise à jour : {updated}
                    </p>
                    <div className="mt-8 h-px bg-champagne/40" aria-hidden="true" />
                </FadeIn>

                {intro && (
                    <FadeIn delay={0.1}>
                        <p className="mt-8 leading-relaxed text-cacao/90">{intro}</p>
                    </FadeIn>
                )}

                <div className="mt-12 space-y-12">
                    {sections.map((section, i) => (
                        <FadeIn key={section.h} delay={Math.min(0.05 * i, 0.3)} y={16}>
                            <section data-testid={`legal-section-${i + 1}`}>
                                <h2 className="flex items-baseline gap-4 font-serif text-xl text-bordeaux sm:text-2xl">
                                    {section.h}
                                </h2>
                                <div className="mt-2 h-px w-12 bg-champagne/60" aria-hidden="true" />
                                <p className="mt-4 whitespace-pre-line text-[15px] leading-relaxed text-cacao/90">
                                    {linkify(section.body)}
                                </p>
                            </section>
                        </FadeIn>
                    ))}
                </div>

                <FadeIn delay={0.1}>
                    <div className="mt-16 border border-champagne/40 bg-white/60 p-6 text-center sm:p-8">
                        <p className="font-serif text-lg italic text-cacao">Une question ?</p>
                        <a
                            href="mailto:contact@mon-eclora.fr"
                            className="mt-2 inline-block text-sm text-bordeaux underline decoration-champagne/60 underline-offset-4 transition-colors duration-300 hover:text-bordeaux-deep"
                            data-testid="legal-contact-link"
                        >
                            contact@mon-eclora.fr
                        </a>
                        {otherHref && (
                            <p className="mt-4 text-xs text-cacao/60">
                                Consulter aussi :{' '}
                                <a href={otherHref} className="underline decoration-champagne/60 underline-offset-2 transition-colors duration-300 hover:text-bordeaux" data-testid="legal-other-link">
                                    {otherLabel}
                                </a>
                            </p>
                        )}
                    </div>
                </FadeIn>
            </article>

            <footer className="border-t border-champagne/40 px-6 py-8 text-center">
                <p className="text-xs text-cacao/50">© 2026 Mon Éclora — Il n'est jamais trop tard pour éclore.</p>
            </footer>
        </div>
    );
}
